/**
 * Bond Tab Component
 * Displays and allows editing of Ghost Bond information
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import type { GhostApplicationDetail } from '../../../../../types/ghostVerification';

interface BondTabProps {
  application: GhostApplicationDetail;
  onUpdate?: () => void;
}


export const BondTab: React.FC<BondTabProps> = ({ application }) => {
  const theme = useTheme();

  const bondInfo = application.bondInfo;


  const handleViewDocument = () => {
    if (bondInfo?.bondDocument?.url) {
      window.open(bondInfo.bondDocument.url, '_blank');
    }
  };

  const handleDownloadDocument = () => {
    if (bondInfo?.bondDocument?.url) {
      const link = document.createElement('a');
      link.href = bondInfo.bondDocument.url;
      link.download = bondInfo.bondDocument.fileName;
      link.click();
    }
  };

  if (!bondInfo) {
    return (
      <Box>
        <Alert severity="info" sx={{ marginBottom: theme.spacing(2) }}>
          No bond information available for this application.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        {/* Bond Details Card */}

        <Grid container spacing={3}>
          {/* Bond Amount */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Bond Amount
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 500,
              }}
            >
              {bondInfo.bondAmount !== undefined
                ? `$${bondInfo.bondAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'N/A'}
            </Typography>
          </Grid>

          {/* Bond Number */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Bond Number
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {bondInfo.bondNumber || 'N/A'}
            </Typography>
          </Grid>

          {/* Bond Company Name */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Bond Company Name
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {bondInfo.bondCompanyName || 'N/A'}
            </Typography>
          </Grid>

          {/* NAIC Number */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              NAIC Number
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {bondInfo.naicNumber || 'N/A'}
            </Typography>
          </Grid>

          {/* Bond Expiry Date */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Bond Expiry Date
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: bondInfo.bondExpiryDate &&
                  new Date(bondInfo.bondExpiryDate) < new Date()
                  ? theme.palette.error.main
                  : theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: bondInfo.bondExpiryDate &&
                  new Date(bondInfo.bondExpiryDate) < new Date()
                  ? 600
                  : 400,
              }}
            >
              {bondInfo.bondExpiryDateText || 'N/A'}
              {bondInfo.bondExpiryDate &&
                new Date(bondInfo.bondExpiryDate) < new Date() && (
                  <Chip
                    label="Expired"
                    size="small"
                    sx={{
                      marginLeft: theme.spacing(1),
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.error.contrastText,
                      fontSize: theme.typography.caption.fontSize,
                      height: 20,
                    }}
                  />
                )}
            </Typography>
          </Grid>

          {/* Bond Document */}
          {bondInfo.bondDocument && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Bond Document
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(2),
                  padding: theme.spacing(2),
                  backgroundColor: theme.customColors.searchBackground,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(0.5),
                    }}
                  >
                    {bondInfo.bondDocument.fileName}
                  </Typography>
                  {bondInfo.bondDocument.fileSize && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.caption.fontSize,
                      }}
                    >
                      {(bondInfo.bondDocument.fileSize / 1024 / 1024).toFixed(2)} MB
                      {bondInfo.bondDocument.uploadedAtText &&
                        ` • Uploaded ${bondInfo.bondDocument.uploadedAtText}`}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
                  <Tooltip title="View Document">
                    <IconButton
                      size="small"
                      onClick={handleViewDocument}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Document">
                    <IconButton
                      size="small"
                      onClick={handleDownloadDocument}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          )}


          {/* Rejection Reason */}
          {bondInfo.rejectionReason && (
            <Grid item xs={12}>
              <Alert severity="error">
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(0.5),
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  Rejection Reason
                </Typography>
                <Typography variant="body2" sx={{ fontSize: theme.typography.body2.fontSize }}>
                  {bondInfo.rejectionReason}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Verification Info */}
          {bondInfo.verifiedBy && (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                Verified by {bondInfo.verifiedBy}
                {bondInfo.verifiedAtText && ` on ${bondInfo.verifiedAtText}`}
              </Typography>
            </Grid>
          )}
        </Grid>

      </Box>

    </>
  );
};
